import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { 
  SocialPost, 
  SocialProfile, 
  SocialCampaign, 
  Queue, 
  SocialPostFormData,
  PostFilters,
  BulkActionResult 
} from '@/types/social'
import socialApi from '../api/socialApi'
// import { useToast } from '@/hooks/use-toast'"

// Posts hooks
export function usePosts(filters?: PostFilters) {
  return useQuery({
    queryKey: ['social-posts', filters],
    queryFn: () => socialApi.getPosts(filters),
    retry: 2,
    staleTime: 30000, // 30 seconds
    onError: (error) => {
      console.error('Failed to fetch posts:', error)
    }
  })
}

export function useCreatePost() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: socialApi.createPost,
    onSuccess: (newPost) => {
      queryClient.invalidateQueries({ queryKey: ['social-posts'] })
      console.log('Post created successfully:', newPost.id)
    },
    onError: (error) => {
      console.error('Failed to create post:', error)
    }
  })
}

export function useUpdatePost() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<SocialPostFormData> }) =>
      socialApi.updatePost(id, data),
    onSuccess: (updatedPost) => {
      queryClient.invalidateQueries({ queryKey: ['social-posts'] })
      console.log('Post updated successfully:', updatedPost.id)
    },
    onError: (error) => {
      console.error('Failed to update post:', error)
    }
  })
}

export function useDeletePost() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: socialApi.deletePost,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['social-posts'] })
    },
    onError: (error) => {
      console.error('Failed to delete post:', error)
    }
  })
}

export function useBulkUpdatePosts() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ postIds, action }: { postIds: string[]; action: string }) =>
      socialApi.bulkUpdatePosts(postIds, action),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['social-posts'] })
    },
    onError: (error) => {
      console.error('Failed to bulk update posts:', error)
    }
  })
}

// Profiles hooks
export function useProfiles() {
  return useQuery({
    queryKey: ['social-profiles'],
    queryFn: socialApi.getProfiles,
    retry: 2,
    staleTime: 60000, // 1 minute
    onError: (error) => {
      console.error('Failed to fetch profiles:', error)
    }
  })
}

export function useCreateProfile() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: socialApi.createProfile,
    onSuccess: (newProfile) => {
      queryClient.invalidateQueries({ queryKey: ['social-profiles'] })
    },
    onError: (error) => {
      console.error('Failed to create profile:', error)
    }
  })
}

export function useUpdateProfile() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<SocialProfile> }) =>
      socialApi.updateProfile(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['social-profiles'] })
    },
    onError: (error) => {
      console.error('Failed to update profile:', error)
    }
  })
}

export function useDeleteProfile() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: socialApi.deleteProfile,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['social-profiles'] })
    },
    onError: (error) => {
      console.error('Failed to delete profile:', error)
    }
  })
}

// Campaigns hooks
export function useCampaigns() {
  return useQuery({
    queryKey: ['social-campaigns'],
    queryFn: socialApi.getCampaigns,
    retry: 1,
    onError: (error) => {
      console.warn('Campaign management not yet implemented in BGV')
    }
  })
}

export function useCreateCampaign() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: socialApi.createCampaign,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['social-campaigns'] })
    },
    onError: (error) => {
    }
  })
}

export function useUpdateCampaign() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<SocialCampaign> }) =>
      socialApi.updateCampaign(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['social-campaigns'] })
    },
    onError: (error) => {
    }
  })
}

export function useDeleteCampaign() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: socialApi.deleteCampaign,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['social-campaigns'] })
    },
    onError: (error) => {
    }
  })
}

// Queues hooks
export function useQueues() {
  return useQuery({
    queryKey: ['social-queues'],
    queryFn: socialApi.getQueues,
    retry: 1,
    onError: (error) => {
      console.warn('Queue management not yet implemented in BGV')
    }
  })
}

export function useCreateQueue() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: socialApi.createQueue,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['social-queues'] })
    },
    onError: (error) => {
    }
  })
}

export function useUpdateQueue() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Queue> }) =>
      socialApi.updateQueue(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['social-queues'] })
    },
    onError: (error) => {
    }
  })
}

export function useDeleteQueue() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: socialApi.deleteQueue,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['social-queues'] })
    },
    onError: (error) => {
    }
  })
}

// AI Content Generation
export function useGenerateContent() {
  return useMutation({
    mutationFn: ({ prompt, context }: { prompt: string; context?: any }) =>
      socialApi.generateContent(prompt, context),
    onSuccess: (result) => {
    },
    onError: (error) => {
      console.error('Failed to generate content:', error)
    }
  })
}

// Analytics
export function useAnalytics(profileId?: string, dateRange?: { start: string; end: string }) {
  return useQuery({
    queryKey: ['social-analytics', profileId, dateRange],
    queryFn: () => socialApi.getAnalytics(profileId, dateRange),
    enabled: !!profileId || profileId === 'all',
    retry: 1,
    onError: (error) => {
      console.warn('Analytics not yet implemented in BGV')
    }
  })
}

// BGV Integration hooks
export function useGalleryImages() {
  return useQuery({
    queryKey: ['gallery-images'],
    queryFn: async () => {
      // This would integrate with the existing gallery system
      // from BGV to fetch approved images for social media use
      console.log('Gallery integration not yet implemented')
      return []
    },
    retry: 1,
    onError: (error) => {
      console.warn('Gallery integration not yet implemented in BGV')
    }
  })
}

export function useApprovedGalleries() {
  return useQuery({
    queryKey: ['approved-galleries'],
    queryFn: async () => {
      // This would fetch galleries that are approved and ready
      // for social media posting from BGV system
      console.log('Gallery integration not yet implemented')
      return []
    },
    retry: 1,
    onError: (error) => {
      console.warn('Gallery integration not yet implemented in BGV')
    }
  })
}

// Automation hooks
export function useAutomationRules() {
  return useQuery({
    queryKey: ['automation-rules'],
    queryFn: async () => {
      // This would integrate with BGV's automation system
      console.log('Automation rules not yet implemented')
      return []
    },
    retry: 1,
    onError: (error) => {
      console.warn('Automation rules not yet implemented in BGV')
    }
  })
}

export function useScheduledPosts() {
  return useQuery({
    queryKey: ['scheduled-posts'],
    queryFn: async () => {
      // Fetch scheduled posts from BGV
      try {
        const posts = await socialApi.getPosts({ status: ['SCHEDULED'] })
        return posts
      } catch (error) {
        console.warn('Failed to fetch scheduled posts:', error)
        return []
      }
    },
    retry: 2,
    onError: (error) => {
      console.warn('Failed to fetch scheduled posts:', error)
    }
  })
}

// BGV-specific hooks
export function usePublishPost() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ postId, testMode = true }: { postId: string; testMode?: boolean }) =>
      socialApi.publishPost(postId, testMode),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['social-posts'] })
    },
    onError: (error) => {
      console.error('Failed to publish post:', error)
    }
  })
}

export function useProcessScheduledPosts() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: socialApi.processScheduledPosts,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['social-posts'] })
      queryClient.invalidateQueries({ queryKey: ['scheduled-posts'] })
    },
    onError: (error) => {
      console.error('Failed to process scheduled posts:', error)
    }
  })
}

export function useBGVConnectionHealth() {
  return useQuery({
    queryKey: ['bgv-connection-health'],
    queryFn: socialApi.getConnectionHealth,
    refetchInterval: 30000, // Check every 30 seconds
    retry: 1
  })
}